import { Component } from "react"
import PropTypes from "prop-types"
import ensureElementIsVisible from "shared/lib/ensure_element_is_visible"
import SearchIndex from "shared/lib/search_index"
import trim from "lodash/trim"

import classnames from "classnames"

export default class DropdownServiceSelector extends Component {
  constructor(props) {
    super(props)

    this.onBlur = this.onBlur.bind(this)
    this.onChangeInput = this.onChangeInput.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onMouseOver = this.onMouseOver.bind(this)

    this.createIndex(props.services)

    this.state = {
      inputValue: "",
      visibleServices: [],
      selected: null,
      selectedWithKeyboard: false,
      newConnectUI: false,
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // If we get an updated list of services, refresh the search index.
    if (this.props.services !== nextProps.services) {
      this.createIndex(nextProps.services)
    }
  }

  componentDidMount() {
    if (document.body.classList.contains("new-connect-ui")) {
      this.setState({ newConnectUI: true })
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.selected !== prevState.selected &&
      this.state.selectedWithKeyboard
    ) {
      ensureElementIsVisible(
        this.autocompleteRef,
        this.autocompleteRef.children[this.state.selected]
      )
    }
  }

  createIndex(services) {
    this.index = new SearchIndex()

    services.forEach(service => {
      // Normalize alternate terms into an array, even if they start out
      // like ["foo,bar"]
      const alternateTerms = service.alternate_search_terms
        ? service.alternate_search_terms.join(",").split(",")
        : []

      this.index.add(service.id, service.name, alternateTerms)
    })
  }

  onBlur() {
    this.setState({ visibleServices: [] })
  }

  onChangeInput(event) {
    this.setState({ inputValue: event.target.value })

    const query = trim(event.target.value)

    if (query === "") {
      this.setState({
        visibleServices: this.props.services,
      })
    } else {
      this.setState({
        visibleServices: this.index.searchAndSelect(query, this.props.services),
      })
    }
  }

  onKeyUp(event) {
    const { selected, visibleServices } = this.state
    const numSuggestions = visibleServices.length

    if (!numSuggestions) {
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()

      const currentSelection = selected === null ? 0 : selected

      // Add an extra `numSuggestions` to avoid weird negative modulus
      this.setState({
        selected: (currentSelection - 1 + numSuggestions) % numSuggestions,
        selectedWithKeyboard: true,
      })
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()

      const currentSelection = selected === null ? -1 : selected

      this.setState({
        selected: (currentSelection + 1) % numSuggestions,
        selectedWithKeyboard: true,
      })
    }

    if (event.key === "Enter") {
      event.preventDefault()
      event.stopPropagation()

      if (selected !== null) {
        this.onSelect(this.state.visibleServices[selected])
      }
    }
  }

  onKeyDown(event) {
    // Separate from onKeyUp to early-prevent form submission
    if (event.key === "Enter") {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  onMouseOver(index) {
    this.setState({ selected: index, selectedWithKeyboard: false })
  }

  onSelect(service) {
    this.props.onSelect(service)
    this.setState({ visibleServices: [] })
  }

  onFocus = event => {
    if (this.state.newConnectUI) {
      event.stopPropagation()
      this.onChangeInput(event)
    }
  }

  tiles() {
    return this.state.visibleServices.map((service, index) => {
      const isSelected = this.state.selected == index
      const selectedClass = isSelected ? "selected" : "not-selected"

      return (
        <li
          key={service.module_name}
          onMouseOver={() => this.onMouseOver(index)}
          className={`dropdown-service-tile ${selectedClass}`}
        >
          <a
            onClick={() => this.onSelect(service)}
            onMouseDown={event => event.preventDefault() /* don't blur */}
          >
            <img
              src={service.lrg_monochrome_image_url}
              alt={service.name}
              title={service.name}
              style={{
                backgroundColor: `#${service.brand_color}`,
              }}
            />
            <span className="service-tile-name">
              <span>{service.name}</span>
            </span>
          </a>
        </li>
      )
    })
  }

  render() {
    const { selectedService } = this.props
    const { inputValue, visibleServices, newConnectUI } = this.state

    return (
      <div
        className={classnames({
          input: true,
          "dropdown-service-selector": true,
          "new-connect-ui": newConnectUI,
        })}
        onKeyUp={this.onKeyUp}
        onBlur={this.onBlur}
      >
        <input
          type="text"
          placeholder={this.props.placeholderCopy}
          onKeyUp={this.onKeyUp}
          onKeyDown={this.onKeyDown}
          onChange={this.onChangeInput}
          onFocus={this.onFocus}
          value={selectedService ? selectedService.name : inputValue}
          disabled={!!selectedService}
          spellCheck={false}
        />

        {visibleServices.length > 0 && (
          <ul className="autocomplete" ref={r => (this.autocompleteRef = r)}>
            {this.tiles()}
          </ul>
        )}
      </div>
    )
  }
}

DropdownServiceSelector.propTypes = {
  services: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      module_name: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ).isRequired,

  selectedService: PropTypes.shape({
    module_name: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    brand_color: PropTypes.string,
    lrg_variant_image_url: PropTypes.string,
  }),

  onSelect: PropTypes.func.isRequired,

  placeholderCopy: PropTypes.string,
}

DropdownServiceSelector.defaultProps = {
  placeholderCopy: "Search services",
}
